import {
  Component,
  inject,
  OnInit,
  HostListener,
  ElementRef,
  signal,
  computed,
  Signal,
  PLATFORM_ID,
  effect,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { CurrentUserService } from '../../../services/current-user.service';
import { ProfileService } from '../../../services/profile-service';
import { UserProfileVTO } from '../../../services/profile-service.model';
import { LanguageService } from '../../../services/language.service';
import { PharmacyService } from '../../../services/pharmacy/pharmacy.service';
import { PharmacyListItem } from '../../../services/pharmacy/pharmacy.model';
import { NotificationsDropdown } from '../notifications-dropdown/notifications-dropdown';
import { NotificationType } from '../../admin-dashboard-component/header/header.model';
import { NotificationService } from '../../../services/notifications/notifications.service';
import { OwnerPharmacyListItem } from '../../../services/pharmacy-credit/pharmacy-credit.model';
import { PharmacyCreditService } from '../../../services/pharmacy-credit/pharmacy-credit';
import { CartIcon } from '../../../shared/ui/icons/cart-icon/cart-icon';
import { Store } from '@ngrx/store';
import { PharmacistCartDetailsDTO } from '../../../pages/user-dashboard/user-cart/user-cart.model';
import { clearCart, loadCartData } from '../../../store/cart/cart.state';
import { PharmacistCartService } from '../../../services/cart/cart.pharmacist';

@Component({
  selector: 'app-header',
  imports: [RouterModule, FormsModule, CommonModule, NotificationsDropdown, CartIcon],
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class Header implements OnInit {
  private currentUserService = inject(CurrentUserService);
  private profileService = inject(ProfileService);
  private router = inject(Router);
  private languageService = inject(LanguageService);
  private pharmacyService = inject(PharmacyService);
  private pharmacyCreditService = inject(PharmacyCreditService);
  private elementRef = inject(ElementRef);
  private notificationService = inject(NotificationService);
  private store = inject(Store);
  private platformId = inject(PLATFORM_ID);
  private pharmacistCartService = inject(PharmacistCartService);

  private readonly SELECTED_PHARMACY_KEY = 'selectedPharmacyId';

  cartData: Signal<PharmacistCartDetailsDTO> = this.store.selectSignal((state) => state.cartData);

  userInitial = signal<string>('U');
  userFullname = signal<string>('User');
  isDropdownOpen = signal<boolean>(false);
  currentLocale = signal<string>('en');
  isNotificationsDropdownOpen = signal<boolean>(false);

  userPharmacies = signal<OwnerPharmacyListItem[]>([]);
  selectedPharmacy = signal<OwnerPharmacyListItem | null>(null);
  // selectedPharmacy = computed(() => this.pharmacyCreditService.selectedPharmacy());

  isPharmacyDropdownOpen = signal<boolean>(false);

  notifications = signal<NotificationType[]>([]);

  hasUnreadNotifications = computed(() => {
    return this.notifications().some((n) => !n.isRead);
  });

  constructor() {
    effect(() => {
      if (this.selectedPharmacy) {
        this.pharmacistCartService.getCartDetails(this.selectedPharmacy()?.id.toString() || '').subscribe({
          next: (res: PharmacistCartDetailsDTO) => {
            this.store.dispatch(loadCartData({ data: res }));
          },
          error: () => {},
        });
      }
    });
  }


cartQuantity = computed(() => {
  return Number(this.cartData()?.totalQuantity ?? 0);
});

  ngOnInit() {
    this.loadUserData();
    this.currentLocale.set(this.languageService.getCurrentLocale());
    this.loadUserPharmacies();

    this.notificationService.getAllNotifications().subscribe({
      next: (res) => {
        this.notifications.set(res.list);
      },
      error: () => {},
    });
  }

  loadUserData() {
    this.profileService.getUserProfile().subscribe({
      next: (user: UserProfileVTO) => {
        const fullname = user.firstName + ' ' + user.lastName;
        this.userFullname.set(fullname);
        this.userInitial.set(fullname.charAt(0).toUpperCase());
      },
      error: (err) => {
        console.error('Failed to load user data:', err);
        this.userInitial.set('U');
      },
    });
  }

  loadUserPharmacies() {
    this.pharmacyCreditService.getMyPharmacies().subscribe({
      next: (res) => {
        this.userPharmacies.set(res.list);
        if (res.list.length > 0) {
          const isBrowser = isPlatformBrowser(this.platformId);
          const savedId = isBrowser ? localStorage.getItem(this.SELECTED_PHARMACY_KEY) : null;
          const savedPharmacy = savedId ? res.list.find((p) => String(p.id) === savedId) : undefined;
          const pharmacyToSelect = savedPharmacy ?? res.list[0];

          this.selectedPharmacy.set(pharmacyToSelect);
          this.pharmacyCreditService.setSelectedPharmacy(pharmacyToSelect);

          if (isBrowser) {
            localStorage.setItem(this.SELECTED_PHARMACY_KEY, String(pharmacyToSelect.id));
          }
        }
      },
      error: (err) => console.error('Failed to load pharmacies:', err),
    });
  }

  selectPharmacy(pharmacy: OwnerPharmacyListItem) {
    this.selectedPharmacy.set(pharmacy);
    this.pharmacyCreditService.setSelectedPharmacy(pharmacy);

    //reset cart
     this.store.dispatch(clearCart());

    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.SELECTED_PHARMACY_KEY, String(pharmacy.id));
    }

    this.isPharmacyDropdownOpen.set(false);
  }

  onCartClick() {
    this.router.navigate(['/user/cart']);
  }

  onNotificationsClick(event: Event) {
    event.stopPropagation();
    this.isNotificationsDropdownOpen.set(!this.isNotificationsDropdownOpen());
  }

  onNotificationsClose() {
    this.isNotificationsDropdownOpen.set(false);
  }

  onMarkAllRead() {
    this.notifications.update((notifications) => notifications.map((n) => ({ ...n, isRead: true })));
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.isDropdownOpen() && !this.elementRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen.set(false);
    }
    const target = event.target as HTMLElement;
    const pharmacyDropdown = target.closest('.pharmacy-dropdown-container');
    if (this.isPharmacyDropdownOpen() && !pharmacyDropdown) {
      this.isPharmacyDropdownOpen.set(false);
    }
    const notificationsContainer = target.closest('.notifications-container');
    if (this.isNotificationsDropdownOpen() && !notificationsContainer) {
      this.isNotificationsDropdownOpen.set(false);
    }
  }

  toggleDropdown(event: Event) {
    event.stopPropagation();
    this.isDropdownOpen.set(!this.isDropdownOpen());
  }

  closeDropdown() {
    this.isDropdownOpen.set(false);
  }

  togglePharmacyDropdown(event: Event) {
    event.stopPropagation();
    this.isPharmacyDropdownOpen.set(!this.isPharmacyDropdownOpen());
  }

  onProfile() {
    this.isDropdownOpen.set(false);
    this.router.navigate(['/user/profile']);
  }

  onLogout() {
    this.currentUserService.logout();
    this.isDropdownOpen.set(false);
    this.router.navigate(['/login']);
  }

  toggleLanguage() {
    this.languageService.toggleLocale();
  }
}
